/**
 * CaseSlaClocks — the Sigma "Quality of Complaints" clocks on a case file
 * (Jess Finlaw, QT-10056; kanban 6c648cab): four stages, five clocks, each a
 * chip whose tone says where it stands. Every reading is the server's
 * (DoorInvestigationRead.clocks); the words are slaClocksView's.
 *
 * Each chip opens (kanban 823ec064 — Jess: "I'm not able to go in and say
 * 'this is the corrective action that we take, let's mark that as
 * complete'"): a detail panel with what the stage means, HOW IT IS DECIDED
 * (the server's one sentence, the same one Merlin quotes), the due / done /
 * who-marked-it lines, the evidence on this case, and — for the four
 * markable stages — the record to edit and a Mark complete / Save / Remove
 * stamp row that writes through POST …/stages/{clock}. Stage 3 opens too,
 * read-only: the watch decides it.
 */
import { useId, useState } from 'react';

import type {
  DoorInvestigationRead,
  DoorSlaClockRead,
  SlaClockKey,
} from '../../services/qcpInvestigations';
import { recordInvestigationStage } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import { failedActionMessage } from './investigationsView';
import {
  SIGMA_STAGES,
  caseClocks,
  clockStatusLine,
  clockTone,
  stageDetail,
  stageEditSpec,
  stageRecordBody,
  stampDay,
  stampRemovable,
  todayIso,
} from './slaClocksView';
import styles from './InvestigationCaseFile.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export default function CaseSlaClocks({
  investigation,
  onUpdated,
}: {
  investigation: DoorInvestigationRead;
  /** The server's full DTO after a stage write; omitted = read-only chips. */
  onUpdated?: (updated: DoorInvestigationRead) => void;
}) {
  const clocks = caseClocks(investigation);
  // The open chip is remembered WITH its case, so a different case shows
  // the strip closed without an effect (the strip may be re-used across
  // cases when embedded in a thread workspace).
  const [opened, setOpened] = useState<{ caseId: string; clock: SlaClockKey } | null>(null);
  const panelId = useId();
  if (clocks.length === 0) return null;
  const open = opened && opened.caseId === investigation.id ? opened.clock : null;
  const selected = open ? clocks.find((c) => c.clock === open) ?? null : null;
  const setOpen = (clock: SlaClockKey | null) =>
    setOpened(clock ? { caseId: investigation.id, clock } : null);

  const toggle = (clock: DoorSlaClockRead) => {
    const next = open === clock.clock ? null : clock.clock;
    setOpen(next);
    if (next) {
      trackEvent('action', 'qcp_case_clock_open', {
        investigation_no: investigation.investigation_no,
        investigation_id: investigation.id,
        clock: clock.clock,
        status: clock.status,
      });
    }
  };

  return (
    <section className={styles.section} aria-label="Sigma clocks" data-testid="sigma-clocks">
      <h4 className={styles.sectionHead}>
        Sigma clocks
        {investigation.clock_started_at && (
          <span className={styles.clockAnchor}>
            {' '}
            · from {new Date(investigation.clock_started_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
        )}
        <span className={styles.clockHint}> · click a clock to see how it is decided</span>
      </h4>
      <ol className={styles.clockStages}>
        {SIGMA_STAGES.map((stage) => {
          const mine = clocks.filter((c) => c.stage === stage.stage);
          return (
            <li key={stage.stage} className={styles.clockStage} data-stage={stage.stage}>
              <span className={styles.clockStageLabel}>
                {stage.stage}. {stage.label}
              </span>
              <span className={styles.clockChips}>
                {mine.map((c) => (
                  <button
                    key={c.clock}
                    type="button"
                    className={styles.clockChip}
                    data-tone={clockTone(c.status)}
                    data-clock={c.clock}
                    data-status={c.status}
                    data-selected={open === c.clock || undefined}
                    aria-expanded={open === c.clock}
                    aria-controls={panelId}
                    title={c.due_at ? `${c.label} — ${clockStatusLine(c)}` : c.label}
                    onClick={() => toggle(c)}
                  >
                    <span className={styles.clockChipLabel}>{c.label}</span>
                    <span className={styles.clockChipStatus}>{clockStatusLine(c)}</span>
                  </button>
                ))}
              </span>
            </li>
          );
        })}
      </ol>
      {selected && (
        <CaseStageDetail
          // Re-mount on a changed stamp so the drafts (the date box above
          // all) reflect what was just written; a text-only save keeps them.
          key={`${selected.clock}:${selected.done_at ?? ''}`}
          id={panelId}
          investigation={investigation}
          clock={selected}
          onUpdated={onUpdated}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

/** One clock opened: the words, the record, and the stamp controls. */
function CaseStageDetail({
  id,
  investigation,
  clock,
  onUpdated,
  onClose,
}: {
  id: string;
  investigation: DoorInvestigationRead;
  clock: DoorSlaClockRead;
  onUpdated?: (updated: DoorInvestigationRead) => void;
  onClose: () => void;
}) {
  const detail = stageDetail(investigation, clock);
  const spec = stageEditSpec(clock.clock);
  // A read-only viewer reads the clock's record and marks nothing (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const editable = detail.markable && Boolean(onUpdated) && investigation.status !== 'cancelled' && !readOnly;
  const currentSummary =
    clock.clock === 'corrective_action'
      ? investigation.corrective_action_summary
      : clock.clock === 'customer_response'
        ? investigation.customer_response_summary
        : null;
  const currentOwner = clock.clock === 'corrective_action' ? investigation.corrective_action_owner : null;
  const [day, setDay] = useState(() => stampDay(clock.done_at));
  const [summary, setSummary] = useState(currentSummary ?? '');
  const [owner, setOwner] = useState(currentOwner ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (action: 'mark' | 'save' | 'clear') => {
    if (busy || !onUpdated) return;
    const body = stageRecordBody(
      clock,
      spec,
      { day, summary, owner },
      { summary: currentSummary, owner: currentOwner },
      action,
    );
    if (!body) return;
    trackEvent('action', `qcp_case_clock_${action}`, {
      investigation_no: investigation.investigation_no,
      investigation_id: investigation.id,
      clock: clock.clock,
      dated: Boolean(body.completed_at),
    });
    setBusy(true);
    setError(null);
    try {
      onUpdated(await recordInvestigationStage(investigation.id, clock.clock, body));
    } catch (e) {
      const what =
        action === 'clear'
          ? `remove the ${clock.label.toLowerCase()} stamp`
          : action === 'mark'
            ? `mark ${clock.label.toLowerCase()}`
            : `save the ${clock.label.toLowerCase()} record`;
      setError(failedActionMessage(what, e));
    } finally {
      setBusy(false);
    }
  };

  const changed =
    (spec.summaryLabel !== null && summary.trim() !== (currentSummary ?? '').trim()) ||
    (spec.hasOwner && owner.trim() !== (currentOwner ?? '').trim()) ||
    (Boolean(clock.done_at) && day !== '' && day !== stampDay(clock.done_at));
  const needsSummary = clock.clock === 'customer_response' && !summary.trim() && !currentSummary;

  return (
    <div
      id={id}
      className={styles.clockDetail}
      data-tone={clockTone(clock.status)}
      data-testid={`clock-detail-${clock.clock}`}
      role="region"
      aria-label={`${clock.label} — details`}
    >
      <div className={styles.clockDetailHead}>
        <strong>
          Stage {clock.stage}: {clock.label}
        </strong>
        <span className={styles.clockDetailStatus}>{clockStatusLine(clock)}</span>
        <button type="button" className={styles.clockDetailClose} onClick={onClose} aria-label="Close details">
          ×
        </button>
      </div>
      {detail.meaning && <p className={styles.clockDetailMeaning}>{detail.meaning}</p>}
      {detail.how && (
        <p className={styles.clockDetailHow} data-testid="clock-how">
          <span className={styles.clockDetailHowLabel}>How this is decided:</span> {detail.how}
        </p>
      )}
      <dl className={styles.clockDetailMeta}>
        {detail.dueLine && (
          <>
            <dt>Deadline</dt>
            <dd>{detail.dueLine}</dd>
          </>
        )}
        {detail.doneLine && (
          <>
            <dt>Reached</dt>
            <dd>{detail.doneLine}</dd>
          </>
        )}
        {detail.recordedLine && (
          <>
            <dt>Who</dt>
            <dd>{detail.recordedLine}</dd>
          </>
        )}
      </dl>
      {detail.evidence.length > 0 && (
        <ul className={styles.clockDetailEvidence} aria-label="Evidence on this case">
          {detail.evidence.map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      )}
      {editable && (
        <form
          className={styles.clockDetailForm}
          onSubmit={(e) => {
            e.preventDefault();
            void send(clock.done_at ? 'save' : 'mark');
          }}
        >
          {spec.summaryLabel && (
            <textarea
              className={styles.textInput}
              rows={2}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={spec.summaryPlaceholder}
              aria-label={spec.summaryLabel}
              disabled={busy}
            />
          )}
          <div className={styles.clockDetailRow}>
            {spec.hasOwner && (
              <input
                type="text"
                className={`${styles.textInput} ${styles.taskOwnerName}`}
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="Owner (a name)"
                aria-label="Corrective action owner"
                disabled={busy}
              />
            )}
            <label className={styles.clockDetailDate}>
              <span>{clock.done_at ? 'Completed on' : 'Completed on (optional)'}</span>
              <input
                type="date"
                className={styles.taskDateInput}
                value={day}
                max={todayIso()}
                onChange={(e) => setDay(e.target.value)}
                aria-label={`${clock.label} completion date`}
                disabled={busy}
              />
            </label>
          </div>
          <div className={styles.clockDetailActions}>
            {clock.done_at ? (
              <button
                type="submit"
                className={styles.taskAddBtn}
                disabled={busy || !changed}
              >
                Save
              </button>
            ) : (
              <button
                type="submit"
                className={styles.taskAddBtn}
                disabled={busy || needsSummary}
                title={needsSummary ? 'Say what the customer was told first.' : undefined}
              >
                {spec.markLabel}
                {day && day !== todayIso() ? ` (${day})` : ''}
              </button>
            )}
            {stampRemovable(clock, spec) && (
              <button
                type="button"
                className={styles.clockDetailClear}
                onClick={() => void send('clear')}
                disabled={busy}
              >
                Remove stamp
              </button>
            )}
            {!clock.done_at && changed && spec.summaryLabel && (
              <button
                type="button"
                className={styles.clockDetailClear}
                onClick={() => void send('save')}
                disabled={busy}
              >
                Save without marking
              </button>
            )}
          </div>
          {error && (
            <div className={styles.transitionError} role="alert" data-testid="case-action-error">
              {error}
            </div>
          )}
        </form>
      )}
      {!editable && detail.markable && investigation.status === 'cancelled' && (
        <p className={styles.hint}>A cancelled case has no running clocks.</p>
      )}
    </div>
  );
}
